import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { environment } from '../../../environments/environment';
import { UserAccessModel } from '../models/user-access.model';
import { PermissionModel } from '../models/permission.model';
import { NullValidationHandler, OAuthService } from 'angular-oauth2-oidc';
import { authConfig } from '../auth-config/auth-config';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isAuthenticated = new BehaviorSubject<boolean>(false);
  userInfo = new BehaviorSubject<UserAccessModel>(null!);
  lightTheme = new BehaviorSubject<boolean>(true);
  carrierID: number;

  constructor(
    private oauthService: OAuthService,
    private injector: Injector,
  ) {}

  implicitFlow(): void {
    this.oauthService.configure(authConfig);
    this.oauthService.tokenValidationHandler = new NullValidationHandler();
    this.oauthService.loadDiscoveryDocument(environment.oidc.discDocURL);
    this.oauthService.tryLoginImplicitFlow().then((hasReceivedTokens) => {
      if (hasReceivedTokens) {
        this.isAuthenticated.next(true);
        return Promise.resolve();
      } else {
        // check if you were previously authenticated
        if (
          this.oauthService.hasValidAccessToken() &&
          this.oauthService.hasValidIdToken()
        ) {
          this.isAuthenticated.next(true);
          return Promise.resolve();
        } else {
          this.isAuthenticated.next(false);
          return new Promise((resolve) => {
            this.oauthService.initLoginFlow();
            window.addEventListener('unload', () => {
              resolve(true);
            });
          });
        }
      }
    });
  }

  logOut(): void {
    this.isAuthenticated.next(false);
    this.oauthService.logOut();
  }

  getClaims(): any {
    return this.oauthService.getIdentityClaims();
  }

  getUserAccess(): Observable<UserAccessModel> {
    const userId = this.getClaims().oid;
    const url = `${environment.userAccess}/${userId}`;
    const apiService = this.injector.get(ApiService);
    return apiService.get<UserAccessModel>(
      url,
      {},
      {},
    );
  }

  switchMode(lightTheme: boolean): void {
    const body = document.getElementsByTagName('body')[0];

    if (lightTheme) {
      body.classList.remove('dark-theme');
      body.classList.add('light-theme');
    } else {
      body.classList.remove('light-theme');
      body.classList.add('dark-theme');
    }

    localStorage.setItem('lightTheme', `${lightTheme}`);
    this.lightTheme.next(lightTheme);
  }

  getPermissionByKey(key: string, perms: PermissionModel[]): PermissionModel[] {
    const tempPerms = perms?.find((item) => item.viewKey === key);
    let temp: PermissionModel[] = [];

    if (tempPerms && tempPerms.children) {
      temp = tempPerms.children;
    }

    return temp;
  }

  /**
   * Returns a Promise with the current user's access token. If not present, tries silent refresh. If not possible, redirects to login.
   */
  async getAccessToken(): Promise<string> {
    const token = this.oauthService.getAccessToken();
    if (token) {
      return token;
    }
    try {
      await this.oauthService.silentRefresh();
      const refreshedToken = this.oauthService.getAccessToken();
      if (refreshedToken) {
        return refreshedToken;
      }
    } catch {
      this.implicitFlow();
    }
    return '';
  }
}
